import type { Role } from '@/shell/api/types'
import { Navigate, useLocation } from 'react-router'
import { useShell } from '@/shell/store'

/**
 * PRD-016 §5 v2.6.2: Agent jobs (the tree row, the page, the client tab and the file block) is an operations and
 * audit view for the CFO and Controller. A Staff accountant, a Viewer and Tax do not see it; the moments a job
 * needs a person (a question, a result to accept, a Review card) reach them as work items in My work. One
 * predicate, so the row, the palette entry, the client tab, the file block and the route gate cannot disagree.
 */
export function canSeeAgentJobs(role: Role): boolean {
  return role === 'cfo' || role === 'controller'
}

/**
 * PRD-016 §5 v2.6.2 / Seb UX review note 50, decision 9: Agent jobs (the row and its route) is CFO and
 * Controller only. A Staff accountant's agent work reaches them as work items in My work (PRD-017 §3.1 kind
 * thread step and review), so the route redirects there instead of 404ing or throwing a permission error —
 * the gate is a friendly bounce, not a wall, the same shape as `NoAccessPage` but with a live destination
 * (audit P1-4: no action a role cannot take should read as broken; a route it cannot see should not either).
 * Old `/threads*` links redirect here first (`ThreadsRedirect` in router.tsx), so a bookmarked or shared link
 * still lands the person on the right side of this gate.
 */
export function AgentJobsGate({ children }: { children: React.ReactNode }) {
  const { bootstrap } = useShell()
  const location = useLocation()
  if (!canSeeAgentJobs(bootstrap.me.role))
    return <Navigate replace to="/my-work" state={{ from: location.pathname + location.search }} />
  return children
}
